import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Send } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";

const empty = {
  email: "",
  name: "",
  password: "",
  referral_code: "",
  tier: "scout",
  parent_scout_id: "",
  active_rate_pct: "",
  passive_rate_pct: "",
};

const TIER_LABEL_KEY = { scout: "tier_scout", refer_close: "tier_refer_close", operator: "tier_operator" };

export default function InviteScoutForm({ lang, tiers = [], onCreated }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [scouts, setScouts] = useState([]);

  useEffect(() => {
    api.get("/operator/scouts").then((r) => setScouts(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const selectedTier = tiers.find((t2) => t2.id === form.tier);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.email || !form.name || !form.password || !form.referral_code) {
      toast.error(t(lang, "required"));
      return;
    }
    setBusy(true);
    try {
      const payload = {
        email: form.email.trim(),
        name: form.name.trim(),
        password: form.password,
        referral_code: form.referral_code.trim().toUpperCase(),
        tier: form.tier,
      };
      if (form.parent_scout_id) payload.parent_scout_id = form.parent_scout_id;
      if (form.active_rate_pct) payload.active_rate_pct = Number(form.active_rate_pct);
      if (form.passive_rate_pct !== "") payload.passive_rate_pct = Number(form.passive_rate_pct);
      const { data } = await api.post("/operator/scouts", payload);
      toast.success(t(lang, "invite_ok"), { description: `${data.name} · ${data.referral_code}` });
      setForm(empty);
      // refresh scouts list
      api.get("/operator/scouts").then((r) => setScouts(r.data)).catch(() => {});
      onCreated && onCreated(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || t(lang, "invite_err");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="eigoo-card p-6 sm:p-7 anim-fade-up anim-delay-3"
      data-testid="invite-scout-card"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl grid place-items-center border border-[#d4a843]/40 bg-[#d4a843]/10">
          <UserPlus className="w-5 h-5 text-[#f3c669]" />
        </div>
        <div>
          <h3 className="font-display text-lg sm:text-xl font-bold">
            {t(lang, "invite_title")}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5 max-w-md">
            {t(lang, "invite_sub")}
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="invite-form">
        <Field label={t(lang, "invite_name")}>
          <input className="eigoo-input" value={form.name} onChange={set("name")} required data-testid="invite-name-input" />
        </Field>
        <Field label={t(lang, "email")}>
          <input type="email" className="eigoo-input" value={form.email} onChange={set("email")} required data-testid="invite-email-input" />
        </Field>
        <Field label={t(lang, "invite_code")}>
          <input className="eigoo-input font-mono uppercase tracking-widest" value={form.referral_code} onChange={set("referral_code")} required maxLength={20} data-testid="invite-code-input" />
        </Field>
        <Field label={t(lang, "password")}>
          <input type="text" className="eigoo-input" value={form.password} onChange={set("password")} required minLength={6} data-testid="invite-password-input" />
        </Field>
        <Field label={t(lang, "invite_tier")}>
          <select className="eigoo-input" value={form.tier} onChange={set("tier")} data-testid="invite-tier-select">
            {tiers.map((tt) => (
              <option key={tt.id} value={tt.id}>
                {t(lang, TIER_LABEL_KEY[tt.id] || "tier_scout")} · {tt.active_pct}% / {tt.passive_pct}%
              </option>
            ))}
          </select>
        </Field>
        <Field label={t(lang, "invite_parent")}>
          <select className="eigoo-input" value={form.parent_scout_id} onChange={set("parent_scout_id")} data-testid="invite-parent-select">
            <option value="">{t(lang, "invite_no_parent")}</option>
            {scouts
              .filter((s) => !s.parent_scout_id)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} · {s.referral_code}
                </option>
              ))}
          </select>
        </Field>

        <Field label={`${t(lang, "invite_active_override")} (${selectedTier?.active_pct}%)`}>
          <input
            type="number"
            step="0.5"
            min="0"
            max="100"
            className="eigoo-input"
            value={form.active_rate_pct}
            onChange={set("active_rate_pct")}
            placeholder={String(selectedTier?.active_pct || 10)}
            data-testid="invite-active-input"
          />
        </Field>
        <Field label={`${t(lang, "invite_passive_override")} (${selectedTier?.passive_pct}%)`}>
          <input
            type="number"
            step="0.5"
            min="0"
            max="100"
            className="eigoo-input"
            value={form.passive_rate_pct}
            onChange={set("passive_rate_pct")}
            placeholder={String(selectedTier?.passive_pct || 0)}
            data-testid="invite-passive-input"
          />
        </Field>

        <div className="sm:col-span-2">
          <button
            type="submit"
            disabled={busy}
            className="gold-btn inline-flex items-center gap-2"
            data-testid="invite-submit-button"
          >
            <Send className="w-4 h-4" />
            {busy ? t(lang, "submitting") : t(lang, "invite_submit")}
          </button>
        </div>
      </form>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
